import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { WorkspacesListResponse } from '@raven/shared-types';

export function AdminTenants() {
  const qc = useQueryClient();
  const { data, isPending } = useQuery({
    queryKey: ['workspaces', 'all'],
    queryFn: () => api.get('/api/workspaces', WorkspacesListResponse),
  });
  const [confirmFor, setConfirmFor] = useState<{ id: string; suspend: boolean } | null>(null);
  const [confirmText, setConfirmText] = useState('');

  const toggle = useMutation({
    mutationFn: (input: { id: string; suspend: boolean }) =>
      api.post(`/api/admin/tenants/${encodeURIComponent(input.id)}/suspend`, {
        suspend: input.suspend,
        confirm_client_id: input.id,
        reason: 'admin action',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspaces'] }),
  });

  if (isPending) return <div className="card text-sm text-slate-500">Loading…</div>;
  return (
    <div className="card">
      <h3 className="text-base font-semibold">Tenants</h3>
      <table className="mt-3 min-w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2">client_id</th>
            <th className="text-left py-2">Name</th>
            <th className="text-left py-2">Status</th>
            <th className="text-right py-2">Action</th>
          </tr>
        </thead>
        <tbody>
          {(data?.workspaces ?? []).map((w) => (
            <tr key={w.workspace_id} className="border-b last:border-b-0">
              <td className="py-2 font-mono text-xs">{w.workspace_id}</td>
              <td className="py-2">{w.name ?? '—'}</td>
              <td className="py-2">
                {w.is_suspended ? (
                  <span className="badge-error">suspended</span>
                ) : (
                  <span className="badge-success">active</span>
                )}
              </td>
              <td className="py-2 text-right">
                <button
                  className="btn-secondary text-xs"
                  onClick={() => {
                    setConfirmFor({ id: w.workspace_id, suspend: !w.is_suspended });
                    setConfirmText('');
                  }}
                >
                  {w.is_suspended ? 'Unsuspend' : 'Suspend'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {confirmFor && (
        <div
          className="fixed inset-0 bg-slate-900/40 flex items-center justify-center px-4"
          onClick={() => setConfirmFor(null)}
        >
          <div
            className="card max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold">
              {confirmFor.suspend ? 'Suspend tenant' : 'Unsuspend tenant'}
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              Type <code className="font-mono">{confirmFor.id}</code> to confirm.
            </p>
            <input
              className="input mt-3"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setConfirmFor(null)}>
                Cancel
              </button>
              <button
                className="btn-danger"
                disabled={confirmText !== confirmFor.id || toggle.isPending}
                onClick={() => {
                  toggle.mutate(confirmFor);
                  setConfirmFor(null);
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
